import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { generalActions } from "../backend/store/reducers/generalReducer";
import { routesTriggerActions } from "../backend/store/reducers/routesReducer";
import { ROUTE_DETAIL_PARAMS } from "../offline/routeParams";
import { useTranslation } from "./useTranslation";

/**
 * Global listener for `routes.optimize-completed`. Mounted once for the
 * logged-in user (typically in App.tsx), so completion toasts and list
 * refreshes fire regardless of which page the user is currently viewing —
 * including the create-route flow that never navigates to RoutePage.
 *
 * Also dispatches getRouteTriggerAC for the completed route_id so any
 * mounted route detail page refreshes its data.
 */
export const useGlobalRouteOptimizeListener = (
  userId: number | null | undefined
) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("general");

  useEffect(() => {
    if (!userId || !(window as any).Echo) return;

    const echo = (window as any).Echo;
    const channel = echo.private(`user.${userId}`);

    const handler = (data: any) => {
      const success: boolean = !!data?.success;
      const message: string | undefined = data?.message;
      const routeId = data?.route_id;

      if (success) {
        dispatch(
          generalActions.successAC({
            message: t("general.optimize_route_success"),
            type: "success",
          })
        );
      } else {
        dispatch(
          generalActions.errorAC({
            message: message || t("general.optimize_route_failed"),
            type: "error",
          })
        );
      }

      dispatch(routesTriggerActions.getRoutesTriggerAC({ address: true }));

      if (routeId !== undefined && routeId !== null) {
        dispatch(
          routesTriggerActions.getRouteTriggerAC({
            id: routeId,
            ...ROUTE_DETAIL_PARAMS,
          })
        );
      }
    };

    channel.listen(".routes.optimize-completed", handler);

    return () => {
      try {
        channel.stopListening(".routes.optimize-completed", handler);
      } catch {
        // ignore cleanup errors
      }
    };
  }, [dispatch, t, userId]);
};
